import { claude, skill } from '../api.js';
import { briefTxt } from '../dom.js';
import { attachReferenceImages, collectPageImages } from '../logic/page-analysis.ts';
import { buildMarketingBriefContext, buildTokenContext } from '../prompts/context.js';
import { ST } from '../state.js';
import { blobToTiles, buildScrapeMessage, initSection, labelTiles } from './helpers.js';

// ── PAGE ANALYZER ────────────────────────────────────────────────
// Per-page analysis orchestration: scrape, screenshot, structure, planner.

/**
 * Analyze a reference page and plan sections.
 * @param {import('../types.ts').Page} page - page with sourceUrl and pageMode set
 * @param {(step: string) => void} [onProgress] - progress callback
 * @returns {Promise<import('../types.ts').Section[]>} planned sections (not including shared header/footer)
 */
export async function analyzePage(page, onProgress) {
  const { sourceUrl, pageMode } = page;

  // Step 1: Scrape + screenshot in parallel (independent network calls)
  onProgress?.('scraping');
  const scrapePromise = fetch('/api/scrape', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url: sourceUrl }),
  }).then(r => r.json());

  const screenshotPromise =
    pageMode === 'match'
      ? fetch(`/api/screenshot?url=${encodeURIComponent(sourceUrl)}`)
          .then(r => (r.ok ? r.blob() : null))
          .then(blob => (blob ? blobToTiles(blob) : null))
          .catch(() => null)
      : Promise.resolve(null);

  // Also pre-fetch home screenshot in parallel
  const homeScreenshotPromise = getHomeScreenshot();

  const [scrapeData, screenshotTiles, homeScreenshot] = await Promise.all([
    scrapePromise,
    screenshotPromise,
    homeScreenshotPromise,
  ]);
  page.pageScrape = scrapeData;
  page.pageScreenshot = screenshotTiles;

  // Step 2: Site-structure extraction (match mode only, needs scrape data)
  if (pageMode === 'match') {
    onProgress?.('extracting');
    const structureSkill = skill('site-structure');
    const scrapeMessage = buildScrapeMessage(page.pageScrape);
    const labeledScreenshot = labelTiles(page.pageScreenshot, sourceUrl);
    try {
      const structureResponse = await claude({
        sys: structureSkill.prompt,
        user: scrapeMessage,
        imgs: labeledScreenshot || undefined,
        tokens: structureSkill.tokens,
        schema: structureSkill.schema,
      });
      page.pageStructure = JSON.parse(structureResponse);
    } catch (error) {
      console.warn('Site-structure extraction failed, falling back to fresh planning:', error);
    }
  }

  // Step 3: Run page planner
  onProgress?.('planning');
  const sections = await planPageSections(page, homeScreenshot);

  // Step 4: Attach reference assets (images + videos) to planned sections
  hydratePageSections(sections, page);

  page.analyzed = true;
  onProgress?.('done');
  return sections;
}

/**
 * Distribute scraped assets (images, videos) to planned sections.
 * @param {import('../types.ts').Section[]} sections
 * @param {import('../types.ts').Page} page
 */
function hydratePageSections(sections, page) {
  const { pageStructure, pageScrape } = page;

  // 1. Attach per-section images from site-structure extraction (match mode)
  if (pageStructure) {
    attachReferenceImages(sections, pageStructure);
  }

  // 2. Attach videos from scrape to video-type sections
  if (pageScrape?.videos?.length) {
    const videoSections = sections.filter(s => s.type === 'video');
    for (let i = 0; i < videoSections.length && i < pageScrape.videos.length; i++) {
      const video = pageScrape.videos[i];
      if (video.type === 'youtube' || video.type === 'vimeo') {
        videoSections[i].videoEmbed = {
          provider: video.type,
          id: video.id,
          thumbnailUrl: video.poster || '',
        };
      }
    }
  }

  // 3. Collect all page images into a flat list
  page.pageImages = pageStructure ? collectPageImages(pageStructure) : [];
}

/**
 * Run the page planner AI to generate section plans for a non-home page.
 * @param {import('../types.ts').Page} page
 * @param {import('../types.ts').UploadedImage[] | null} homeScreenshot
 * @returns {Promise<import('../types.ts').Section[]>}
 */
async function planPageSections(page, homeScreenshot) {
  const skillConfig = skill('page-planner');
  const concept = ST.concept;

  // Design context
  const tokenContext = buildTokenContext(concept);
  const designVisionBlock = `<design_vision>
Concept: ${concept.designVision.concept}
Page flow: ${concept.designVision.pageFlow}
Signature element: ${concept.designVision.signatureElement}
</design_vision>`;

  // Brief context
  const briefContext =
    ST.conceptContext ||
    (ST.marketingBrief ? buildMarketingBriefContext(ST.marketingBrief) : briefTxt());

  // Page-specific context
  let pageContext = '';
  if (page.pageMode === 'match' && page.pageStructure) {
    // Filter out header/footer — they are shared from home, not planned per-page
    const contentSections = page.pageStructure.filter(
      s => s.type !== 'header' && s.type !== 'footer',
    );
    const structureText = contentSections
      .map(section => `[${section.type}] ${section.label || ''}\n${section.content || ''}`)
      .join('\n\n');
    pageContext = `<reference_page_structure>
URL: ${page.sourceUrl}
Mode: Match — replicate this structure with the new brand design.
Reference has ${contentSections.length} content sections. Output exactly ${contentSections.length} sections.

${structureText}
</reference_page_structure>`;
  } else if (page.pageMode === 'fresh' && page.pageScrape) {
    pageContext = `<reference_page_content>
URL: ${page.sourceUrl}
Page title: ${page.pageScrape.title}
Mode: Fresh design — use this content as context but plan optimal section structure.

${page.pageScrape.metaDescription || ''}
${page.pageScrape.rawHtml ? `<page_html>\n${page.pageScrape.rawHtml.slice(0, 15_000)}\n</page_html>` : ''}
</reference_page_content>`;
  } else {
    pageContext = `<page_context>
Page name: ${page.name}
Plan sections for this page based on the business context and design vision.
</page_context>`;
  }

  // Home page context — list home sections so planner avoids duplication
  const homeSections = ST.pages[0].sects
    .filter(s => s.type !== 'header' && s.type !== 'footer')
    .map(s => `- ${s.type}: ${s.title} — ${s.purpose || ''}`)
    .join('\n');
  const homePageContext = `<home_page_sections>
The home page has these sections (avoid repeating this content):
${homeSections}
</home_page_sections>`;

  // Other existing pages — avoid cross-page duplication
  const otherPagesEntries = ST.pages
    .filter(
      (p, i) =>
        i > 0 && p.id !== page.id && p.sects.some(s => s.type !== 'header' && s.type !== 'footer'),
    )
    .map(p => {
      const sectionTypes = p.sects
        .filter(s => s.type !== 'header' && s.type !== 'footer')
        .map(s => s.type)
        .join(', ');
      return `- ${p.name}: ${sectionTypes}`;
    });
  const otherPagesContext = otherPagesEntries.length
    ? `<other_pages>\n${otherPagesEntries.join('\n')}\n</other_pages>`
    : '';

  const userMessage = [
    briefContext,
    tokenContext,
    designVisionBlock,
    pageContext,
    homePageContext,
    otherPagesContext,
  ]
    .filter(Boolean)
    .join('\n\n');

  // Vision inputs
  const images = [];
  if (homeScreenshot?.length) {
    images.push(
      ...homeScreenshot.map((tile, i) => ({
        ...tile,
        label:
          i === 0
            ? 'Built home page — match this visual style and design quality'
            : '(home page screenshot tile)',
      })),
    );
  }
  if (page.pageScreenshot?.length) {
    images.push(
      ...page.pageScreenshot.map((tile, i) => ({
        ...tile,
        label:
          i === 0
            ? `Reference page screenshot: ${page.sourceUrl} — replicate this structure`
            : '(reference page screenshot tile)',
      })),
    );
  }

  const response = await claude({
    sys: skillConfig.prompt,
    user: userMessage,
    imgs: images.length ? images : undefined,
    tokens: skillConfig.tokens,
    model: skillConfig.model,
    temp: skillConfig.temp,
    effort: skillConfig.effort,
    thinking: skillConfig.thinking,
    schema: skillConfig.schema,
  });

  const result = JSON.parse(response);
  // Accumulate sections so sectionId sees previously created ones (avoids duplicate IDs)
  const created = [...page.sects];
  return (result.sections || []).map(s => {
    const sect = initSection(s, created);
    created.push(sect);
    return sect;
  });
}

/**
 * Get (or generate) a cached screenshot of the built home page.
 * @returns {Promise<import('../types.ts').UploadedImage[] | null>}
 */
export async function getHomeScreenshot() {
  const homePage = ST.pages[0];
  if (!homePage?.assembled) {
    return null;
  }

  // Return cached if available
  if (ST.homePageScreenshot) {
    return ST.homePageScreenshot;
  }

  // Generate screenshot from assembled HTML
  try {
    const response = await fetch('/api/screenshot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html: homePage.assembled }),
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    ST.homePageScreenshot = await blobToTiles(blob);
    return ST.homePageScreenshot;
  } catch {
    return null;
  }
}

/**
 * Suggest pages for a site using AI.
 * @returns {Promise<Array<{name: string, path: string, purpose: string, priority: string}>>}
 */
export async function suggestPages() {
  const skillConfig = skill('suggest-pages');
  const brief = ST.marketingBrief;
  const existingPageNames = ST.pages.map(p => p.name).join(', ');

  const businessSummary = [
    brief?.companyName && `Company: ${brief.companyName}`,
    brief?.industry && `Industry: ${brief.industry}`,
    brief?.businessOverview && `Overview: ${brief.businessOverview}`,
    brief?.facts?.services?.length &&
      `Services: ${brief.facts.services.map(s => s.name).join(', ')}`,
  ]
    .filter(Boolean)
    .join('\n');

  const response = await claude({
    sys: skillConfig.prompt,
    user: `<business>\n${businessSummary}\n</business>\n\n<existing_pages>${existingPageNames}</existing_pages>\n\nSuggest additional pages for this website.`,
    tokens: skillConfig.tokens,
    model: skillConfig.model,
    temp: skillConfig.temp,
    schema: skillConfig.schema,
  });
  return JSON.parse(response).pages;
}
